/**
 * Écran « En attente de l'accord parental » (RGPD art.8).
 *
 * Affiché pendant l'onboarding d'un élève < 15 ans ET, via la garde du
 * StudentLayout, à chaque connexion tant que le consentement n'est pas `active`.
 * Permet de renvoyer le mail au parent et d'actualiser une fois validé.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { requestParentalConsent } from '@/lib/parental-consent';

type SendState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent' }
  | { kind: 'no_smtp' }
  | { kind: 'error' };

export function ConsentWaitingScreen({
  email,
  onRefresh,
  onEditEmail,
}: {
  email: string;
  onRefresh?: () => void;
  /** Onboarding : revenir corriger l'adresse saisie (faute de frappe). */
  onEditEmail?: () => void;
}) {
  const navigate = useNavigate();
  const [send, setSend] = useState<SendState>({ kind: 'idle' });

  async function handleResend() {
    setSend({ kind: 'sending' });
    try {
      const res = await requestParentalConsent();
      setSend({ kind: res.email_sent ? 'sent' : 'no_smtp' });
    } catch {
      setSend({ kind: 'error' });
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-transparent p-6">
      <div className="w-full max-w-md">
        <Card className="border-ink-100/60 shadow-lg">
          <CardHeader>
            <CardTitle>En attente de l'accord parental ⏳</CardTitle>
            <CardDescription>
              Un email a été envoyé à <strong className="text-ink-700">{email}</strong>. Demande à
              ton parent de cliquer sur le lien pour valider ton inscription.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="info">
              <AlertDescription>
                Dès que ton parent aura validé, tu pourras accéder à Jeraisonne. Tu peux fermer
                cette page et revenir plus tard.
              </AlertDescription>
            </Alert>

            {send.kind === 'sent' && (
              <Alert variant="success">
                <AlertDescription>Email renvoyé ✅</AlertDescription>
              </Alert>
            )}
            {send.kind === 'no_smtp' && (
              <Alert variant="info">
                <AlertDescription>
                  Demande enregistrée. L'email partira dès que la messagerie sera disponible.
                </AlertDescription>
              </Alert>
            )}
            {send.kind === 'error' && (
              <Alert variant="error">
                <AlertDescription>
                  L'envoi a échoué. Vérifie ta connexion et réessaie.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                loading={send.kind === 'sending'}
                onClick={handleResend}
              >
                Renvoyer l'email
              </Button>
              {onRefresh && (
                <Button type="button" variant="secondary" size="lg" fullWidth onClick={onRefresh}>
                  J'ai déjà validé — actualiser
                </Button>
              )}
              {onEditEmail && (
                <button
                  type="button"
                  onClick={onEditEmail}
                  className="text-primary-700 min-h-[2.75rem] text-center text-sm font-semibold"
                >
                  Ce n’est pas la bonne adresse ? La corriger
                </button>
              )}
              <button
                type="button"
                onClick={handleSignOut}
                className="text-ink-500 hover:text-ink-700 min-h-[2.75rem] text-center text-sm underline"
              >
                Se déconnecter
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
